import { useState, useRef, DragEvent, ChangeEvent } from 'react';
import { api } from '../api';

export interface UploadedDoc {
  id: string;
  name: string;
  size: number;
  status: 'uploading' | 'processing' | 'ready' | 'failed';
  progress: number;
  error?: string;
}

interface DocumentUploaderProps {
  documents: UploadedDoc[];
  onDocumentsChange: (docs: UploadedDoc[] | ((prev: UploadedDoc[]) => UploadedDoc[])) => void;
  uploadEndpoint?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const _ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
  'text/csv',
];

const ACCEPTED_EXTENSIONS = ['.pdf', '.docx', '.txt', '.md', '.csv'];

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocumentUploader({
  documents,
  onDocumentsChange,
  uploadEndpoint = '/kb/upload',
}: DocumentUploaderProps) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFiles = async (files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newDocs: UploadedDoc[] = fileArray.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      name: f.name,
      size: f.size,
      status: 'uploading' as const,
      progress: 0,
    }));

    const updatedDocs = [...documents, ...newDocs];
    onDocumentsChange(updatedDocs);

    // Upload each file
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i];
      const doc = newDocs[i];

      const res = await api.upload(uploadEndpoint, file, (pct) => {
        onDocumentsChange((prev: UploadedDoc[]) =>
          prev.map((d) => (d.id === doc.id ? { ...d, progress: pct } : d))
        );
      });

      onDocumentsChange((prev: UploadedDoc[]) =>
        prev.map((d) => {
          if (d.id === doc.id) {
            return res.ok
              ? { ...d, status: 'processing' as const, progress: 100 }
              : { ...d, status: 'failed' as const, error: res.error, progress: 100 };
          }
          return d;
        })
      );

      // Simulate processing -> ready (in real app, poll for status)
      if (res.ok) {
        setTimeout(() => {
          onDocumentsChange((prev: UploadedDoc[]) =>
            prev.map((d) => (d.id === doc.id ? { ...d, status: 'ready' as const } : d))
          );
        }, 2000);
      }
    }
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length > 0) {
      handleFiles(e.dataTransfer.files);
    }
  };

  const onDragOver = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };

  const onFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(e.target.files);
      e.target.value = '';
    }
  };

  const removeDoc = (id: string) => {
    onDocumentsChange(documents.filter((d) => d.id !== id));
  };

  const statusBadge = (status: UploadedDoc['status']) => {
    switch (status) {
      case 'uploading':
        return <span className="badge badge-blue">Uploading</span>;
      case 'processing':
        return <span className="badge badge-yellow">Processing</span>;
      case 'ready':
        return <span className="badge badge-green">Ready</span>;
      case 'failed':
        return <span className="badge badge-red">Failed</span>;
    }
  };

  return (
    <div className="space-y-4">
      {/* Drop Zone */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={() => inputRef.current?.click()}
        className={`
          border-2 border-dashed rounded-card p-8 text-center cursor-pointer transition-all
          ${
            dragOver
              ? 'border-accent bg-accent/5'
              : 'border-border hover:border-border-hover hover:bg-white/[0.01]'
          }
        `}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPTED_EXTENSIONS.join(',')}
          onChange={onFileSelect}
          className="hidden"
        />
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          className={`mx-auto mb-3 ${dragOver ? 'text-accent' : 'text-text-muted'}`}
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        <p className={`text-sm font-medium mb-1 ${dragOver ? 'text-accent' : 'text-text-primary'}`}>
          Drop files here or click to browse
        </p>
        <p className="text-xs text-text-muted">
          Accepted: PDF, DOCX, TXT, MD, CSV (max 10MB each)
        </p>
      </div>

      {/* Uploaded Files */}
      {documents.length > 0 && (
        <div className="space-y-2">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center gap-3 p-3 rounded-card bg-surface-tertiary border border-border"
            >
              {/* File icon */}
              <div className="w-8 h-8 rounded bg-surface-secondary flex items-center justify-center shrink-0">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-text-muted">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                  <polyline points="14 2 14 8 20 8" />
                </svg>
              </div>

              {/* File info */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary truncate">{doc.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-text-muted">{formatSize(doc.size)}</span>
                  {statusBadge(doc.status)}
                </div>
                {doc.status === 'uploading' && (
                  <div className="mt-1.5 h-1 bg-surface-secondary rounded-full overflow-hidden">
                    <div
                      className="h-full bg-accent rounded-full transition-all duration-300"
                      style={{ width: `${doc.progress}%` }}
                    />
                  </div>
                )}
                {doc.error && (
                  <p className="text-xs text-red-400 mt-0.5">{doc.error}</p>
                )}
              </div>

              {/* Remove button */}
              <button
                onClick={() => removeDoc(doc.id)}
                className="text-text-muted hover:text-red-400 p-1 transition-colors shrink-0"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
